import React, { useCallback, useEffect, useState } from 'react';
import { Mail, Send, X } from 'lucide-react';
import organizationsService from '../services/organizationsService';

/**
 * Invitations that have been sent and not yet accepted.
 *
 * Without this the Members tab could only ever show people who were already
 * in. An admin who invited somebody last week had no way to answer "did I
 * actually invite them, and to what role" short of inviting them again — which
 * the server refuses while one is pending, so the only feedback was an error
 * about a thing they could not see.
 *
 * Shown BELOW the member list rather than mixed into it: a pending invitation
 * is not a member, and a list that blurred the two would make the seat count
 * look wrong to whoever is paying for them.
 */
export default function OrganizationPendingInvitations({ orgId, canEdit, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  // Which invitation was just resent, so the row can confirm it. Nothing else
  // visibly changes on a resend, and silence reads as a dead button.
  const [sent, setSent] = useState(null);

  const load = useCallback(async () => {
    // The service already swallows failures into []. A pending-invitations
    // list that could not load looks identical to none pending, which is the
    // benign reading, and the member list above it stays usable either way.
    setRows(await organizationsService.getInvitations(orgId));
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await organizationsService.getInvitations(orgId);
      if (!cancelled) setRows(next);
    })();
    return () => {
      cancelled = true;
    };
    // refreshKey lets the parent pull the list again after it sends one, so a
    // new invitation appears without a reload.
  }, [orgId, refreshKey]);

  const resend = async (invitation) => {
    setBusyId(invitation.id);
    setError('');
    setSent(null);
    try {
      await organizationsService.resendInvitation(orgId, invitation.id);
      setSent(invitation.id);
    } catch (e) {
      const status = e?.response?.status;
      // 410 and 409 mean different things to the admin: one needs a fresh
      // invitation, the other means somebody already cancelled this one.
      if (status === 410) setError('That invitation has expired — send a new one.');
      else if (status === 409) setError('That invitation is no longer pending.');
      else setError('Could not resend that invitation.');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (invitation) => {
    setBusyId(invitation.id);
    setError('');
    try {
      await organizationsService.revokeInvitation(orgId, invitation.id);
      await load();
    } catch {
      setError('Could not revoke that invitation.');
    } finally {
      setBusyId(null);
    }
  };

  if (rows.length === 0) return null;

  return (
    <div className="org-pending">
      <h4 className="org-pending-title">
        Pending invitations <span className="org-pending-count">{rows.length}</span>
      </h4>

      {rows.map((inv) => (
        <div key={inv.id} className="org-pending-row">
          <Mail size={14} aria-hidden className="org-pending-icon" />
          <span className="org-pending-email">{inv.email}</span>
          <span className={`orgx-role orgx-role--${inv.role}`}>{inv.role}</span>
          {inv.expires_at && (
            <span className="org-pending-expiry">
              expires {new Date(inv.expires_at).toLocaleDateString('en-GB')}
            </span>
          )}
          {sent === inv.id && <span className="org-pending-sent">Sent</span>}
          {canEdit && (
            <button
              type="button"
              className="org-icon-btn"
              title="Resend invitation"
              aria-label={`Resend the invitation to ${inv.email}`}
              disabled={busyId === inv.id}
              onClick={() => resend(inv)}
            >
              <Send size={13} aria-hidden />
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="org-icon-btn"
              title="Revoke invitation"
              aria-label={`Revoke the invitation to ${inv.email}`}
              disabled={busyId === inv.id}
              onClick={() => revoke(inv)}
            >
              <X size={14} aria-hidden />
            </button>
          )}
        </div>
      ))}

      {error && <p className="org-error">{error}</p>}
    </div>
  );
}
